
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import vue2Dropzone, { DropZoneOptions, DropZone } from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import API from '@/lib/API';
import {
  Lead, LeadType, Task, User, Category, Tag, PermissionCode,
} from '@/types';
import UserGroups from '@/components/UserGroups.vue';

@Component({
  components: {
    UserGroups,
    vueDropzone: vue2Dropzone,
  },
})
export default class LeadAssignDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Array) readonly childTasks!: Task[];

  dialog = false
  valid = false
  loading = false

  newAttachments: number[] = []
  removedAttachments: number[] = []

  dz: DropZone | null = null

  dzOptions: DropZoneOptions = {
    url: '/upload/sopralluogoTecnico',
    addRemoveLinks: true,
    removeType: 'server',
    dictCancelUpload: 'Annulla caricamento',
    dictCancelUploadConfirmation: 'Confermi di voler annullare il caricamento?',
    dictRemoveFile: 'Rimuovi',
    dictDefaultMessage: 'Trascinare qui i file da caricare<br><small>oppure cliccare</small>',
  }

  childCount = 0
  childType: LeadType = 'installatore-figlia'
  childName: string | null = null
  childRequester: number | null = null
  childOwners: number[] = []
  childCategories: number[] = []
  childTags: number[] = []

  @Watch('value')
  onValueChange(val: boolean) {
    console.log('LeadAssignDialog: onValueChange()'); // eslint-disable-line no-console

    if (val === true) {
      this.newAttachments = [];
      this.removedAttachments = [];

      if (this.dz) {
        console.log('LeadAssignDialog: onValueChange() - Dropzone esistente, elimino tutti i file'); // eslint-disable-line no-console

        this.dzOptions.removeType = 'client';
        this.dz.removeAllFiles();
        this.dzOptions.removeType = 'server';
      }

      this.childRequester = this.user.id;
      this.childOwners = [];
      this.childCategories = [];
      this.childTags = [];
      this.childCount = this.childTasks[0].childCount ? this.childTasks[0].childCount : 1;
      this.childType = this.childTasks[0].childType ? this.childTasks[0].childType : 'installatore-figlia';
      this.childName = this.childTasks[0].childName ? this.childTasks[0].childName : null;
    }
    this.dialog = val;
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get users() {
    return this.$store.getters.users;
  }

  get hasRequester() {
    return this.childRequester;
  }

  get hasOwners() {
    return Array.isArray(this.childOwners) && this.childOwners.length > 0;
  }

  get categories(): Category[] {
    return this.$store.getters.categories;
  }

  get tags(): Tag[] {
    return this.$store.getters.tags;
  }

  get visibleCategories() {
    return Array.isArray(this.categories)
      ? this.categories.filter((c) => !c.hidden)
      : [];
  }

  get visibleTags() {
    return Array.isArray(this.tags)
      ? this.tags.filter((t) => !t.hidden)
      : [];
  }

  get noSelectedRequester() {
    return !this.hasRequester;
  }

  get noSelectedOwners() {
    return !this.hasOwners;
  }

  get flatCategories() {
    return this.lead
      && 'flatCategories' in this.lead
      && typeof this.lead.flatCategories === 'string'
      ? this.lead.flatCategories : null;
  }

  get flatTags() {
    return this.lead
      && 'flatTags' in this.lead
      && typeof this.lead.flatTags === 'string'
      ? this.lead.flatTags : null;
  }

  get hasCategories() {
    return this.flatCategories
      && this.flatCategories.length > 2;
  }

  get hasTags() {
    return this.flatTags
      && this.flatTags.length > 2;
  }

  get leadCategories(): number[] {
    return this.hasCategories && this.flatCategories
      ? this.flatCategories.substring(1, this.flatCategories.length - 1).split(':')
        .map((s: string) => Number(s))
      : [];
  }

  get leadTags(): number[] {
    return this.hasTags && this.flatTags
      ? this.flatTags.substring(1, this.flatTags.length - 1).split(':')
        .map((s: string) => Number(s))
      : [];
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  leadCategory(categoryId: number) {
    if (!this.categories) return null;

    return this.categories.find((c: Category) => c.id === categoryId);
  }

  leadTag(tagId: number) {
    if (!this.tags) return null;

    return this.tags.find((tag) => tag.id === tagId);
  }

  tagColor(tagId: number): string {
    const tag = this.leadTag(tagId);

    const baseColor = tag?.color ? tag.color : 'light-blue';

    // Se il tag è nascosto o disattivato, schiarisce il colore
    if (tag?.hidden || tag?.isActive === false) return `${baseColor} lighten-3`;

    return baseColor;
  }

  removeCategory(category: Category) {
    console.log('LeadAssignDialog: removeCategory() category =', category); // eslint-disable-line no-console

    const newCategories = this.childCategories.filter((categoryId) => categoryId !== category.id);

    this.childCategories = newCategories;
  }

  removeTag(tag: Tag) {
    console.log('LeadAssignDialog: removeTag() tag =', tag); // eslint-disable-line no-console

    const newTags = this.childTags.filter((tagId) => tagId !== tag.id);

    this.childTags = newTags;
  }

  dzMounted() {
    console.log('LeadAssignDialog: dzMounted()'); // eslint-disable-line no-console

    this.dz = 'attachmentsDropzone' in this.$refs ? this.$refs.attachmentsDropzone : null;
  }

  success(file: any, response: any) {
    console.log('LeadAssignDialog: success()'); // eslint-disable-line no-console

    console.log(`LeadAssignDialog: success() - Aggiunto il file ${file.name}`); // eslint-disable-line no-console

    if (response) {
      console.log('LeadAssignDialog: success() - response =', response); // eslint-disable-line no-console

      this.newAttachments.push(response.id);
    }
  }

  removeOwner(owner: User) {
    console.log('LeadAssignDialog: removeOwner() owner =', owner); // eslint-disable-line no-console

    const newOwners = this.childOwners.filter((ownerUserId) => ownerUserId !== owner.id);

    this.childOwners = newOwners;
  }

  removedFile(file: any) {
    console.log('LeadAssignDialog: removedFile()'); // eslint-disable-line no-console

    console.log(`LeadAssignDialog: removedFile() - Richiesta la rimozione del file ${file.name}`); // eslint-disable-line no-console

    let serverId: number;
    if (file.xhr) {
      const jsonResponse = JSON.parse(file.xhr.response);
      serverId = jsonResponse.id;
    } else {
      serverId = file.serverId;
    }

    console.log(`LeadAssignDialog: removedFile() - serverId = ${serverId}`); // eslint-disable-line no-console

    this.removedAttachments.push(serverId);

    const aIndex = this.newAttachments.findIndex((a) => a === serverId);
    if (aIndex > -1) {
      this.newAttachments.splice(aIndex, 1);
    }
  }

  async removeAttachments() {
    console.log('LeadAssignDialog: removeAttachments()'); // eslint-disable-line no-console

    if (this.removedAttachments.length > 0) {
      await API.removeLeadAttachments(this.lead.id, {
        attachments: this.removedAttachments,
      });
    }
  }

  async removeAllAttachments() {
    console.log('LeadAssignDialog: removeAllAttachments()'); // eslint-disable-line no-console

    if (this.newAttachments.length > 0) {
      await API.removeLeadAttachments(this.lead.id, {
        attachments: [...this.newAttachments, ...this.removedAttachments],
      });
    }
  }

  async onCancel() {
    console.log('LeadAssignDialog: onCancel()'); // eslint-disable-line no-console

    this.dialog = false;
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }

  async onAssign() {
    console.log('LeadAssignDialog: onAssign()'); // eslint-disable-line no-console

    if (!this.childRequester) return;

    this.loading = true;

    // Avverte che l'assegnazione è iniziata
    this.$emit('start');

    await this.removeAttachments();

    // Assegna la lead figlia senza attendere il risultato
    API.assignChildLead(this.lead.id, {
      type: this.childType,
      name: this.childName,
      count: this.childCount,
      requester: { id: this.childRequester },
      owners: this.childOwners,
      categories: this.childCategories,
      tags: this.childTags,
      attachments: this.newAttachments,
    })
      .then(() => {
        // Quando l'assegnazione è completata, avverte
        this.$emit('done');
      });

    this.dialog = false;
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }
}
